"use client";

import { STORY } from "@/data/about-content";
import { useContent } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";

interface AboutContent {
  storyTitle: Bilingual;
  storyBody: Bilingual;
  milestones: { id: string; year: string; label: Bilingual }[];
}

/** TAHAWER — heritage + our story + milestone timeline. */
export default function AboutStory() {
  const locale = useLocale() as Locale;
  const { data } = useContent<AboutContent>("about");
  const title = data?.storyTitle?.[locale] ?? STORY.title[locale];
  const body = data?.storyBody?.[locale] ?? STORY.body[locale];
  const timeline = data?.milestones ?? [];

  return (
    <section id="tahawer" className="scroll-mt-44 border-b border-black/10 dark:border-white/10">
      <div className="mx-auto grid max-w-360 gap-12 px-6 py-20 md:py-28 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <div>
          <span className="text-[11px] uppercase tracking-[0.22em] text-accent">{STORY.eyebrow[locale]}</span>
          <h2 className="mt-4 font-bebas text-[clamp(2.6rem,6vw,5rem)] uppercase leading-[0.9] text-ash dark:text-white">
            {title}
          </h2>
        </div>

        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[clamp(1.15rem,2vw,1.6rem)] font-light leading-snug text-ash dark:text-white"
          >
            {STORY.lead[locale]}
          </motion.p>
          <p className="mt-6 leading-relaxed text-ash/55 dark:text-white/55">{body}</p>

          {/* Timeline */}
          {timeline.length > 0 && (
          <div className="relative mt-14">
            <div className="absolute inset-x-0 top-1.5 h-px bg-black/10 dark:bg-white/10" />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {timeline.map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className="relative"
                >
                  <span className="block h-3 w-3 rounded-full bg-accent shadow-[0_0_0_4px_rgba(200,16,46,0.15)]" />
                  <div className="mt-4 font-bebas text-2xl text-accent">{m.year}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-ash/50 dark:text-white/50">{m.label[locale]}</div>
                </motion.div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </section>
  );
}
